import { useEffect, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import type { TreeFile } from '../../lib/projectsApi';
import { FileKindIcon } from './FolderView';
import { saveFile } from '../../lib/saveFile';
import { previewTypeOf } from '../../lib/filePreview';

/**
 * Preview one stored file in a modal: PDFs inline in an iframe, images as a
 * lightbox, video/audio in the native players, plain-text files as text,
 * everything else a download link. Shared by the resource tabs (projects,
 * clients, suppliers, people) and the chat file card.
 */
export type PreviewableFile = Pick<TreeFile, 'name' | 'kind' | 'downloadUrl'>;

// Text previews stop here; a log file of hundreds of MB has no business in a modal.
const MAX_TEXT_PREVIEW = 512 * 1024;

function TextPreview({ url }: { url: string }) {
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'ok'; text: string; cut: boolean } | { status: 'error' }
  >({ status: 'loading' });

  useEffect(() => {
    const ctrl = new AbortController();
    setState({ status: 'loading' });
    fetch(url, { credentials: 'same-origin', signal: ctrl.signal })
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error(String(res.status)))))
      .then((text) =>
        setState({
          status: 'ok',
          text: text.slice(0, MAX_TEXT_PREVIEW),
          cut: text.length > MAX_TEXT_PREVIEW,
        }),
      )
      .catch(() => {
        if (!ctrl.signal.aborted) setState({ status: 'error' });
      });
    return () => ctrl.abort();
  }, [url]);

  if (state.status === 'loading') {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-gray-500">Se încarcă…</div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-gray-500">
        Fișierul nu a putut fi citit.
      </div>
    );
  }
  return (
    <div className="p-4">
      <pre className="whitespace-pre-wrap break-words rounded-md bg-white p-4 font-mono text-xs text-gray-800 shadow-sm">
        {state.text}
      </pre>
      {state.cut && (
        <p className="mt-2 text-xs text-gray-500">
          Afișăm doar începutul fișierului. Descarcă-l pentru tot conținutul.
        </p>
      )}
    </div>
  );
}

/** The preview area alone, so modals with extra chrome (invoice metadata) reuse it. */
export function FilePreviewBody({ file }: { file: PreviewableFile }) {
  if (!file.downloadUrl) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-gray-500">
        Niciun URL de previzualizare disponibil.
      </div>
    );
  }
  const url = file.downloadUrl;
  switch (previewTypeOf(file)) {
    case 'pdf':
      return <iframe title={file.name} src={url} className="h-[70vh] w-full" />;
    case 'image':
      return (
        <div className="flex items-center justify-center p-4">
          <img src={url} alt={file.name} className="max-h-[70vh] max-w-full" />
        </div>
      );
    case 'video':
      return (
        <div className="flex items-center justify-center bg-black p-2">
          <video src={url} controls className="max-h-[70vh] max-w-full" />
        </div>
      );
    case 'audio':
      return (
        <div className="flex h-48 items-center justify-center p-4">
          <audio src={url} controls className="w-full max-w-lg" />
        </div>
      );
    case 'text':
      return <TextPreview url={url} />;
    default:
      return (
        <div className="flex h-64 flex-col items-center justify-center gap-3 text-sm text-gray-500">
          <FileKindIcon kind={file.kind} className="h-10 w-10 text-gray-400" />
          <span>Previzualizarea nu este disponibilă pentru acest tip de fișier.</span>
          <button
            type="button"
            onClick={() => void saveFile(url, file.name)}
            className="inline-flex items-center gap-1 text-blue-600 underline"
          >
            <Download size={14} /> Descarcă în schimb
          </button>
        </div>
      );
  }
}

/**
 * Prev/next through the files of the open folder, by button or ←/→; Escape
 * closes. Either handler may be omitted at the ends of the list.
 */
export interface PreviewNavigation {
  onPrev?: () => void;
  onNext?: () => void;
  /** e.g. "3 / 12", shown between the arrows. */
  position?: string;
}

export function FilePreviewFrame({
  file,
  onClose,
  nav,
  children,
}: {
  file: PreviewableFile;
  onClose: () => void;
  nav?: PreviewNavigation;
  /** Rendered between the header and the preview (e.g. invoice metadata). */
  children?: ReactNode;
}) {
  const onPrev = nav?.onPrev;
  const onNext = nav?.onNext;
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && onPrev) onPrev();
      else if (e.key === 'ArrowRight' && onNext) onNext();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <FileKindIcon kind={file.kind} className="h-5 w-5 shrink-0 text-gray-500" />
            <span className="truncate text-sm font-medium text-gray-900">{file.name}</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {nav && (nav.onPrev || nav.onNext) && (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={nav.onPrev}
                  disabled={!nav.onPrev}
                  className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                  aria-label="Fișierul anterior"
                  title="Anterior (←)"
                >
                  <ChevronLeft size={18} />
                </button>
                {nav.position && (
                  <span className="text-xs tabular-nums text-gray-500">{nav.position}</span>
                )}
                <button
                  type="button"
                  onClick={nav.onNext}
                  disabled={!nav.onNext}
                  className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                  aria-label="Fișierul următor"
                  title="Următor (→)"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
            {file.downloadUrl && (
              <button
                type="button"
                onClick={() => void saveFile(file.downloadUrl as string, file.name)}
                className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                <Download size={14} /> Descarcă
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100"
              aria-label="Închide previzualizarea"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        {children}
        <div className="flex-1 overflow-auto bg-gray-100">
          {/* key: a new file remounts the players and the text fetch */}
          <FilePreviewBody key={file.downloadUrl ?? file.name} file={file} />
        </div>
      </div>
    </div>
  );
}

export function FilePreviewModal({
  file,
  onClose,
  nav,
}: {
  file: PreviewableFile;
  onClose: () => void;
  nav?: PreviewNavigation;
}) {
  return <FilePreviewFrame file={file} onClose={onClose} nav={nav} />;
}
